import type {
  CodeMapRegion,
  CoverageMode,
  GuideItem,
  PrimaryNature,
  ReviewGuide,
} from "./guide";
import { assessChewfileCopy } from "./copy-quality";
import { emptyGlossary, type Glossary } from "./glossary";
import { defaultChewConfig, inferredPathNature, type ChewConfig } from "./config";
import type { GitDiffManifest, GitManifestFile } from "./git-manifest";
import type { ProgressProjection } from "./progress";

export type DiagnosticSeverity = "error" | "warning" | "info";
export type CoverageState =
  | "directly-presented"
  | "structurally-summarized"
  | "intentionally-skimmed"
  | "excluded-with-reason"
  | "unseen"
  | "stale";

export interface SourceFileSnapshot {
  base: string;
  head: string;
  language?: string;
  provenance?: string;
  binary?: boolean;
  encoding?: "utf8" | "base64";
  oldPath?: string;
  newPath?: string;
  baseBlobSha?: string;
  headBlobSha?: string;
}

export interface SourceBundle {
  schemaVersion?: 1 | 2;
  source: ReviewGuide["source"];
  manifest?: GitDiffManifest;
  notice?: string;
  files: Record<string, SourceFileSnapshot>;
}

export interface CompileDiagnostic {
  severity: DiagnosticSeverity;
  code: string;
  message: string;
  path?: string;
  regionId?: string;
}

export interface ChangedLineIdentity {
  side: "LEFT" | "RIGHT";
  line: number;
  identity: string;
}

export interface CompiledRegion {
  id: string;
  path: string;
  nature: PrimaryNature;
  concerns: CodeMapRegion["concerns"];
  scrutiny: CodeMapRegion["scrutiny"];
  treatment: CodeMapRegion["treatment"];
  exclusionReason?: string;
  layerId?: string;
  coverageMode?: CoverageMode;
  verification: "verified" | "source-unavailable";
  changedLoc?: number;
  addedLoc?: number;
  deletedLoc?: number;
  contextHash?: string;
  baseBlobSha?: string;
  headBlobSha?: string;
  changedLines: ChangedLineIdentity[];
}

export interface CodeMapCompilation {
  sourceKey: string;
  compiledAt: string;
  regions: CompiledRegion[];
  diagnostics: CompileDiagnostic[];
  summary: {
    expectedFiles: number;
    sourceFiles: number;
    verifiedFiles: number;
    unverifiedFiles: number;
    changedLoc: number;
    classifiedChangedLoc: number;
  };
  unverifiedPaths: string[];
}

export interface ReceiptCategory {
  nature: PrimaryNature;
  changedLoc: number;
  unknownFiles: number;
  states: Record<CoverageState, number>;
  regions: Array<CompiledRegion & { coverageState: CoverageState }>;
}

export interface ReviewReceipt {
  categories: ReceiptCategory[];
  unverifiedFiles: number;
  knownChangedLoc: number;
  productionChangedLoc: number;
  productionDirectLoc: number;
  directProductionPercent: number;
  unseenKnownLoc: number;
  staleKnownLoc: number;
}

export interface DiffSizeSummary {
  totalChangedLoc: number;
  totalAdditions: number;
  totalDeletions: number;
  effectiveChangedLoc: number;
  effectiveAdditions: number;
  effectiveDeletions: number;
  excludedChangedLoc: number;
  excludedNatures: PrimaryNature[];
}

export type ResolvedGuideItem = GuideItem & {
  sourceItemId: string;
  regionId: string;
  path: string;
  scope: "file" | "range";
  side: "LEFT" | "RIGHT";
  startLine: number;
  endLine: number;
  changedLoc: number;
  changedLineNumbers: number[];
  contextHash: string;
  nature: PrimaryNature;
  concerns: CodeMapRegion["concerns"];
  scrutiny: CodeMapRegion["scrutiny"];
};

export type TourStop = ResolvedGuideItem & {
  stopId: string;
  stopIndex: number;
  stopCount: number;
};

const encoder = new TextEncoder();

function lines(value: string): string[] {
  if (value === "") return [];
  const normalized = value.replace(/\r\n/g, "\n");
  return normalized.endsWith("\n")
    ? normalized.slice(0, -1).split("\n")
    : normalized.split("\n");
}

function hex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function digest(algorithm: "SHA-1" | "SHA-256", value: Uint8Array): Promise<string> {
  const bytes = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer;
  return hex(await crypto.subtle.digest(algorithm, bytes));
}

async function gitBlobSha(value: string | Uint8Array): Promise<string> {
  const body = typeof value === "string" ? encoder.encode(value) : value;
  const header = encoder.encode(`blob ${body.byteLength}\0`);
  const input = new Uint8Array(header.byteLength + body.byteLength);
  input.set(header, 0);
  input.set(body, header.byteLength);
  return digest("SHA-1", input);
}

function snapshotBytes(value: string, encoding: SourceFileSnapshot["encoding"]): Uint8Array {
  if (encoding !== "base64") return encoder.encode(value);
  const decoded = atob(value);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

function snapshotText(value: string, encoding: SourceFileSnapshot["encoding"]): string {
  if (encoding !== "base64") return value;
  return new TextDecoder().decode(snapshotBytes(value, encoding));
}

async function lineIdentity(side: "LEFT" | "RIGHT", line: number, value: string): Promise<string> {
  return `${side}:${line}:${(await digest("SHA-256", encoder.encode(value))).slice(0, 16)}`;
}

export async function changedLines(base: string, head: string): Promise<ChangedLineIdentity[]> {
  const left = lines(base);
  const right = lines(head);
  const matrix = Array.from({ length: left.length + 1 }, () => new Uint32Array(right.length + 1));
  for (let leftIndex = left.length - 1; leftIndex >= 0; leftIndex -= 1) {
    for (let rightIndex = right.length - 1; rightIndex >= 0; rightIndex -= 1) {
      matrix[leftIndex]![rightIndex] = left[leftIndex] === right[rightIndex]
        ? matrix[leftIndex + 1]![rightIndex + 1]! + 1
        : Math.max(matrix[leftIndex + 1]![rightIndex]!, matrix[leftIndex]![rightIndex + 1]!);
    }
  }

  const changed: Array<{ side: "LEFT" | "RIGHT"; line: number; value: string }> = [];
  let leftIndex = 0;
  let rightIndex = 0;
  while (leftIndex < left.length && rightIndex < right.length) {
    if (left[leftIndex] === right[rightIndex]) {
      leftIndex += 1;
      rightIndex += 1;
    } else if (matrix[leftIndex + 1]![rightIndex]! >= matrix[leftIndex]![rightIndex + 1]!) {
      changed.push({ side: "LEFT", line: leftIndex + 1, value: left[leftIndex]! });
      leftIndex += 1;
    } else {
      changed.push({ side: "RIGHT", line: rightIndex + 1, value: right[rightIndex]! });
      rightIndex += 1;
    }
  }
  while (leftIndex < left.length) {
    changed.push({ side: "LEFT", line: leftIndex + 1, value: left[leftIndex]! });
    leftIndex += 1;
  }
  while (rightIndex < right.length) {
    changed.push({ side: "RIGHT", line: rightIndex + 1, value: right[rightIndex]! });
    rightIndex += 1;
  }

  return Promise.all(
    changed.map(async ({ side, line, value }) => ({
      side,
      line,
      identity: await lineIdentity(side, line, value),
    })),
  );
}

function diagnostic(
  severity: DiagnosticSeverity,
  code: string,
  message: string,
  extra: Pick<CompileDiagnostic, "path" | "regionId"> = {},
): CompileDiagnostic {
  return { severity, code, message, ...extra };
}

function regionOwnsLine(region: CodeMapRegion, changed: ChangedLineIdentity): boolean {
  return (
    region.scope === "range" &&
    region.side === changed.side &&
    changed.line >= region.startLine &&
    changed.line <= region.endLine
  );
}

export async function compileCodeMap(
  guide: ReviewGuide,
  bundle: SourceBundle,
  config: ChewConfig = defaultChewConfig,
  glossary: Glossary = emptyGlossary,
): Promise<CodeMapCompilation> {
  const diagnostics: CompileDiagnostic[] = [...assessChewfileCopy(guide, glossary)];
  if (bundle.source.baseSha !== guide.source.baseSha || bundle.source.headSha !== guide.source.headSha) {
    diagnostics.push(
      diagnostic("error", "SOURCE_IDENTITY", "Source bundle does not match the Chewfile base/head commits."),
    );
  }

  const manifest = bundle.manifest;
  const manifestByPath = new Map<string, GitManifestFile>();
  if (manifest) {
    if (
      manifest.repository !== guide.repository ||
      manifest.baseSha !== guide.source.baseSha ||
      manifest.headSha !== guide.source.headSha
    ) {
      diagnostics.push(
        diagnostic("error", "MANIFEST_IDENTITY", "Git manifest repository or commits do not match the Chewfile."),
      );
    }
    for (const file of manifest.files) {
      if (manifestByPath.has(file.path)) {
        diagnostics.push(diagnostic("error", "MANIFEST_DUPLICATE_PATH", "Git manifest contains a duplicate path.", { path: file.path }));
      }
      manifestByPath.set(file.path, file);
    }
    const manifestChangedLoc = manifest.files.reduce((sum, file) => sum + file.changedLines.length, 0);
    const manifestAdditions = manifest.files.reduce((sum, file) => sum + (file.additions ?? 0), 0);
    const manifestDeletions = manifest.files.reduce((sum, file) => sum + (file.deletions ?? 0), 0);
    if (
      manifest.totals.files !== manifest.files.length ||
      manifest.totals.changedLoc !== manifestChangedLoc ||
      manifest.totals.additions !== manifestAdditions ||
      manifest.totals.deletions !== manifestDeletions
    ) {
      diagnostics.push(diagnostic("error", "MANIFEST_TOTALS", "Git manifest totals do not reconcile to its files."));
    }
  }

  const coveragePaths = new Set(guide.coverage.files.map((entry) => entry.path));
  const regionsByPath = new Map<string, CodeMapRegion[]>();
  for (const region of guide.codeMap.regions) {
    const group = regionsByPath.get(region.path) ?? [];
    group.push(region);
    regionsByPath.set(region.path, group);
    if (region.confidence === "low") {
      diagnostics.push(
        diagnostic("warning", "LOW_CONFIDENCE", "This semantic classification is marked low confidence.", {
          path: region.path,
          regionId: region.id,
        }),
      );
    }
  }

  const primaryTour = new Map<string, { layerId: string; coverageMode: CoverageMode }>();
  for (const layer of guide.layers) {
    for (const item of layer.items) {
      if (!item.primary) continue;
      for (const regionId of item.regionIds) {
        primaryTour.set(regionId, { layerId: layer.id, coverageMode: item.coverageMode });
      }
    }
  }

  const compiledById = new Map<string, CompiledRegion>();
  for (const region of guide.codeMap.regions) {
    const tour = primaryTour.get(region.id);
    compiledById.set(region.id, {
      id: region.id,
      path: region.path,
      nature: region.nature,
      concerns: region.concerns,
      scrutiny: region.scrutiny,
      treatment: region.treatment,
      exclusionReason: region.exclusionReason,
      layerId: tour?.layerId,
      coverageMode: tour?.coverageMode,
      verification: "source-unavailable",
      changedLines: [],
    });
  }

  let changedLoc = 0;
  let classifiedChangedLoc = 0;
  const verifiedPaths = new Set<string>();
  for (const [path, file] of Object.entries(bundle.files)) {
    if (!coveragePaths.has(path)) {
      diagnostics.push(diagnostic("error", "UNKNOWN_SOURCE_PATH", "Source contains a path absent from file coverage.", { path }));
      continue;
    }
    const regions = regionsByPath.get(path) ?? [];
    if (regions.length === 0) {
      diagnostics.push(diagnostic("error", "UNCLASSIFIED_SOURCE", "Changed source has no codemap region.", { path }));
      continue;
    }
    const defaults = regions.filter((region) => region.scope === "file");
    if (defaults.length !== 1) {
      diagnostics.push(
        diagnostic("error", "FILE_DEFAULT", `Expected exactly one whole-file default; found ${defaults.length}.`, { path }),
      );
      continue;
    }
    const inferredNature = inferredPathNature(path, config);
    if (inferredNature && defaults[0]?.nature !== inferredNature) {
      diagnostics.push(diagnostic(
        "info",
        "PATH_MATCHER_OVERRIDE",
        `Path matcher suggests '${inferredNature}', while the codemap default is '${defaults[0]?.nature}'. The codemap remains authoritative.`,
        { path, regionId: defaults[0]?.id },
      ));
    }

    const baseText = snapshotText(file.base, file.encoding);
    const headText = snapshotText(file.head, file.encoding);
    const leftLines = lines(baseText).length;
    const rightLines = lines(headText).length;
    const annotations = guide.annotations.filter((annotation) => annotation.path === path);
    for (const region of regions) {
      if (
        region.scope === "range" &&
        (region.endLine > (region.side === "LEFT" ? leftLines : rightLines))
      ) {
        diagnostics.push(
          diagnostic(
            "error",
            "RANGE_BOUNDS",
            `Range ${region.startLine}–${region.endLine} exceeds the ${region.side} blob.`,
            { path, regionId: region.id },
          ),
        );
      }
    }
    for (const annotation of annotations) {
      const lineCount = annotation.side === "LEFT" ? leftLines : rightLines;
      if (annotation.endLine > lineCount) {
        diagnostics.push(diagnostic(
          "error",
          "ANNOTATION_RANGE_BOUNDS",
          `Annotation range ${annotation.startLine}–${annotation.endLine} exceeds the ${annotation.side} blob.`,
          { path },
        ));
      }
    }

    const manifestFile = manifestByPath.get(path);
    if (manifest && !manifestFile) {
      diagnostics.push(diagnostic("error", "MANIFEST_PATH_MISSING", "Source file is absent from the Git manifest.", { path }));
    }
    const [fallbackChanges, computedBaseBlobSha, computedHeadBlobSha] = await Promise.all([
      manifest || file.binary ? Promise.resolve([]) : changedLines(baseText, headText),
      gitBlobSha(snapshotBytes(file.base, file.encoding)),
      gitBlobSha(snapshotBytes(file.head, file.encoding)),
    ]);
    const fileChanges = manifestFile?.changedLines ?? fallbackChanges;
    for (const annotation of annotations) {
      if (!fileChanges.some((line) =>
        line.side === annotation.side && line.line >= annotation.startLine && line.line <= annotation.endLine,
      )) {
        diagnostics.push(diagnostic(
          "info",
          "ANNOTATION_UNCHANGED_RANGE",
          "Annotation range contains no changed lines and will appear only when surrounding context is expanded.",
          { path },
        ));
      }
    }
    const baseBlobSha = file.baseBlobSha ?? computedBaseBlobSha;
    const headBlobSha = file.headBlobSha ?? computedHeadBlobSha;
    if (file.baseBlobSha && file.baseBlobSha !== computedBaseBlobSha) {
      diagnostics.push(diagnostic("error", "BASE_BLOB_SHA", "Recorded base blob SHA does not match the source.", { path }));
    }
    if (file.headBlobSha && file.headBlobSha !== computedHeadBlobSha) {
      diagnostics.push(diagnostic("error", "HEAD_BLOB_SHA", "Recorded head blob SHA does not match the source.", { path }));
    }
    if (
      manifestFile &&
      (manifestFile.oldBlobSha !== file.baseBlobSha || manifestFile.newBlobSha !== file.headBlobSha)
    ) {
      diagnostics.push(diagnostic("error", "MANIFEST_BLOB_SHA", "Source blob SHAs do not match the Git manifest.", { path }));
    }
    const baseLines = lines(baseText);
    const headLines = lines(headText);
    const lineDiagnostics = await Promise.all(
      fileChanges.map(async (changed) => {
        const sourceLines = changed.side === "LEFT" ? baseLines : headLines;
        const content = sourceLines[changed.line - 1];
        if (content === undefined) {
          return diagnostic(
            "error",
            "MANIFEST_LINE_BOUNDS",
            `${changed.side} line ${changed.line} is outside the source blob.`,
            { path },
          );
        }
        const expectedIdentity = await lineIdentity(changed.side, changed.line, content);
        return expectedIdentity === changed.identity
          ? undefined
          : diagnostic(
              "error",
              "MANIFEST_LINE_HASH",
              `${changed.side} line ${changed.line} has a mutated identity.`,
              { path },
            );
      }),
    );
    diagnostics.push(...lineDiagnostics.filter((entry) => entry !== undefined));
    changedLoc += fileChanges.length;
    verifiedPaths.add(path);

    const assigned = new Map<string, ChangedLineIdentity[]>();
    for (const changed of fileChanges) {
      const overrides = regions.filter((region) => regionOwnsLine(region, changed));
      if (overrides.length > 1) {
        diagnostics.push(
          diagnostic(
            "error",
            "PRIMARY_OVERLAP",
            `${changed.side} line ${changed.line} belongs to ${overrides.length} primary ranges.`,
            { path },
          ),
        );
        continue;
      }
      const owner = overrides[0] ?? defaults[0];
      if (!owner) {
        diagnostics.push(
          diagnostic("error", "PRIMARY_GAP", `${changed.side} line ${changed.line} has no primary nature.`, { path }),
        );
        continue;
      }
      const owned = assigned.get(owner.id) ?? [];
      owned.push(changed);
      assigned.set(owner.id, owned);
      classifiedChangedLoc += 1;
    }

    for (const region of regions) {
      const owned = assigned.get(region.id) ?? [];
      const compiled = compiledById.get(region.id)!;
      compiled.verification = "verified";
      compiled.changedLines = owned;
      compiled.addedLoc = owned.filter((line) => line.side === "RIGHT").length;
      compiled.deletedLoc = owned.filter((line) => line.side === "LEFT").length;
      compiled.changedLoc = owned.length;
      compiled.baseBlobSha = baseBlobSha;
      compiled.headBlobSha = headBlobSha;
      compiled.contextHash = `ctx-${(
        await digest(
          "SHA-256",
          encoder.encode(`${region.id}\n${baseBlobSha}\n${headBlobSha}\n${owned.map((line) => line.identity).join("\n")}`),
        )
      ).slice(0, 24)}`;
    }
  }

  const unverifiedPaths = guide.coverage.files
    .map((entry) => entry.path)
    .filter((path) => !verifiedPaths.has(path));
  const missingCodemapPaths = unverifiedPaths.filter((path) => !regionsByPath.has(path));
  if (manifest) {
    for (const path of manifestByPath.keys()) {
      if (!coveragePaths.has(path)) {
        diagnostics.push(diagnostic("error", "MANIFEST_UNACCOUNTED_PATH", "Git changed path is absent from Chewfile coverage.", { path }));
      }
    }
    for (const path of coveragePaths) {
      if (!manifestByPath.has(path)) {
        diagnostics.push(diagnostic("error", "COVERAGE_NOT_CHANGED", "Chewfile coverage path is absent from the Git diff manifest.", { path }));
      }
    }
  }
  if (unverifiedPaths.length > 0) {
    diagnostics.push(
      diagnostic(
        guide.codeMap.verification === "exact" ? "error" : "warning",
        "SOURCE_PARTIAL",
        `${unverifiedPaths.length} of ${guide.coverage.expectedFiles} changed files have no bundled source facts.`,
      ),
    );
  }
  if (missingCodemapPaths.length > 0) {
    diagnostics.push(
      diagnostic(
        guide.codeMap.verification === "exact" ? "error" : "warning",
        "CODEMAP_PARTIAL",
        `${missingCodemapPaths.length} source-unavailable files have no verified codemap region.`,
      ),
    );
  }

  return {
    sourceKey: `${guide.source.baseSha}..${guide.source.headSha}`,
    compiledAt: new Date().toISOString(),
    regions: [...compiledById.values()],
    diagnostics,
    summary: {
      expectedFiles: guide.coverage.expectedFiles,
      sourceFiles: Object.keys(bundle.files).length,
      verifiedFiles: verifiedPaths.size,
      unverifiedFiles: unverifiedPaths.length,
      changedLoc,
      classifiedChangedLoc,
    },
    unverifiedPaths,
  };
}

export function summarizeDiffSize(
  compilation: CodeMapCompilation,
  config: ChewConfig = defaultChewConfig,
): DiffSizeSummary {
  const excluded = new Set<PrimaryNature>(config.effectiveDiff.excludeNatures);
  let totalAdditions = 0;
  let totalDeletions = 0;
  let effectiveAdditions = 0;
  let effectiveDeletions = 0;
  for (const region of compilation.regions) {
    const additions = region.addedLoc ?? 0;
    const deletions = region.deletedLoc ?? 0;
    totalAdditions += additions;
    totalDeletions += deletions;
    if (!excluded.has(region.nature)) {
      effectiveAdditions += additions;
      effectiveDeletions += deletions;
    }
  }
  const effectiveChangedLoc = effectiveAdditions + effectiveDeletions;
  return {
    totalChangedLoc: compilation.summary.changedLoc,
    totalAdditions,
    totalDeletions,
    effectiveChangedLoc,
    effectiveAdditions,
    effectiveDeletions,
    excludedChangedLoc: Math.max(0, compilation.summary.changedLoc - effectiveChangedLoc),
    excludedNatures: [...excluded],
  };
}

/**
 * How much review a diff actually asks for.
 *
 * Raw LOC is a poor proxy: 800 lines of generated protobuf is a glance, 80 lines rewriting a
 * tenancy check is not. This weights every changed line by the scrutiny its region declared and
 * by whether it carries a concern that has to be reasoned about rather than read.
 *
 * The unit is deliberately "standard-scrutiny LOC" so the number stays in the same universe as
 * the LOC counts beside it and can be read as "this reviews like a diff of N lines".
 */
export const reviewWeightFactors = {
  scrutiny: { scan: 0.25, standard: 1, deep: 2.5 },
  /** Excluded natures still cost a skim, but nothing like reasoning about them. */
  excludedNature: 0.2,
  /** Concerns that need judgement rather than reading. */
  demandingConcern: 1.5,
} as const;

const demandingConcerns = new Set([
  "authorization",
  "tenancy",
  "security",
  "concurrency",
  "irreversible-effect",
  "migration",
  "privacy",
]);

export type ReviewWeightBand = "light" | "moderate" | "heavy" | "very-heavy";

export interface ReviewWeightContribution {
  regionId: string;
  path: string;
  changedLoc: number;
  weighted: number;
}

export interface ReviewWeightSummary {
  /** Weighted LOC, expressed as standard-scrutiny equivalent. */
  weightedLoc: number;
  band: ReviewWeightBand;
  /** weightedLoc / effectiveChangedLoc — above 1 means the diff reads heavier than it measures. */
  densityRatio: number;
  /** Heaviest regions first, so a UI can name what is driving the number. */
  drivers: ReviewWeightContribution[];
}

const bandThresholds: ReadonlyArray<[ReviewWeightBand, number]> = [
  ["light", 250],
  ["moderate", 900],
  ["heavy", 2_200],
];

export function reviewWeightBand(weightedLoc: number): ReviewWeightBand {
  for (const [band, ceiling] of bandThresholds) {
    if (weightedLoc <= ceiling) return band;
  }
  return "very-heavy";
}

export function summarizeReviewWeight(
  compilation: CodeMapCompilation,
  config: ChewConfig = defaultChewConfig,
): ReviewWeightSummary {
  const excluded = new Set<PrimaryNature>(config.effectiveDiff.excludeNatures);
  const contributions: ReviewWeightContribution[] = [];
  let weightedLoc = 0;

  for (const region of compilation.regions) {
    const changedLoc = region.changedLoc ?? 0;
    if (changedLoc === 0) continue;
    // An excluded region is skimmed whatever its declared scrutiny, so the exclusion
    // multiplier applies on top rather than instead.
    let weight = reviewWeightFactors.scrutiny[region.scrutiny];
    if (excluded.has(region.nature)) weight *= reviewWeightFactors.excludedNature;
    if (region.concerns.some((concern) => demandingConcerns.has(concern))) {
      weight *= reviewWeightFactors.demandingConcern;
    }
    const weighted = changedLoc * weight;
    weightedLoc += weighted;
    contributions.push({ regionId: region.id, path: region.path, changedLoc, weighted });
  }

  const rounded = Math.round(weightedLoc);
  const effective = summarizeDiffSize(compilation, config).effectiveChangedLoc;
  return {
    weightedLoc: rounded,
    band: reviewWeightBand(rounded),
    densityRatio: effective === 0 ? 0 : Number((rounded / effective).toFixed(2)),
    drivers: contributions.sort((left, right) => right.weighted - left.weighted).slice(0, 5),
  };
}

function completedLayer(_guide: ReviewGuide, progress: ProgressProjection, layerId: string): boolean {
  const status = progress.layerStatuses[layerId];
  return (["done", "question", "problem"] as const).includes(status as "done");
}

export function coverageStateForRegion(
  guide: ReviewGuide,
  region: CompiledRegion,
  progress: ProgressProjection,
): CoverageState {
  if (region.treatment === "excluded") return "excluded-with-reason";
  if (!region.layerId || !region.coverageMode) return "unseen";
  if (
    progress.layerStatuses[region.layerId] === "stale" ||
    progress.regionStatuses[region.id] === "stale"
  ) return "stale";
  if (region.coverageMode === "direct") {
    return progress.regionStatuses[region.id] === "presented" ? "directly-presented" : "unseen";
  }
  if (!completedLayer(guide, progress, region.layerId)) return "unseen";
  return {
    direct: "directly-presented",
    "structured-summary": "structurally-summarized",
    "intentional-skim": "intentionally-skimmed",
  }[region.coverageMode] as CoverageState;
}

export function buildReviewReceipt(
  guide: ReviewGuide,
  compilation: CodeMapCompilation,
  progress: ProgressProjection,
  config: ChewConfig = defaultChewConfig,
): ReviewReceipt {
  const categories = new Map<PrimaryNature, ReceiptCategory>();
  const states = (): Record<CoverageState, number> => ({
    "directly-presented": 0,
    "structurally-summarized": 0,
    "intentionally-skimmed": 0,
    "excluded-with-reason": 0,
    unseen: 0,
    stale: 0,
  });
  for (const region of compilation.regions) {
    const category = categories.get(region.nature) ?? {
      nature: region.nature,
      changedLoc: 0,
      unknownFiles: 0,
      states: states(),
      regions: [],
    };
    const coverageState = coverageStateForRegion(guide, region, progress);
    const loc = region.changedLoc ?? 0;
    category.changedLoc += loc;
    if (region.verification === "source-unavailable") category.unknownFiles += 1;
    category.states[coverageState] += loc;
    category.regions.push({ ...region, coverageState });
    categories.set(region.nature, category);
  }

  const excludedFromProduction = new Set<PrimaryNature>(config.effectiveDiff.excludeNatures);
  let productionChangedLoc = 0;
  let productionDirectLoc = 0;
  let unseenKnownLoc = 0;
  let staleKnownLoc = 0;
  for (const category of categories.values()) {
    unseenKnownLoc += category.states.unseen;
    staleKnownLoc += category.states.stale;
    if (!excludedFromProduction.has(category.nature)) {
      productionChangedLoc += category.changedLoc;
      productionDirectLoc += category.states["directly-presented"];
    }
  }

  return {
    categories: [...categories.values()].sort((left, right) => right.changedLoc - left.changedLoc),
    unverifiedFiles: compilation.summary.unverifiedFiles,
    knownChangedLoc: compilation.summary.changedLoc,
    productionChangedLoc,
    productionDirectLoc,
    directProductionPercent:
      productionChangedLoc === 0 ? 0 : Math.round((productionDirectLoc / productionChangedLoc) * 100),
    unseenKnownLoc,
    staleKnownLoc,
  };
}

export function resolveGuideItem(
  guide: ReviewGuide,
  compilation: CodeMapCompilation,
  item: GuideItem,
  regionId: string = item.regionIds[0]!,
): ResolvedGuideItem {
  const semantic = guide.codeMap.regions.find((region) => region.id === regionId);
  const compiled = compilation.regions.find((region) => region.id === regionId);
  if (!semantic || !compiled) throw new Error(`${item.id}: codemap region could not be resolved`);
  const preferredSide = compiled.changedLines.some((line) => line.side === "RIGHT") ? "RIGHT" : "LEFT";
  const ownedLines = compiled.changedLines
    .filter((line) => line.side === preferredSide)
    .map((line) => line.line);
  return {
    ...item,
    id: item.regionIds.length === 1 ? item.id : `${item.id}--${regionId}`,
    sourceItemId: item.id,
    regionId,
    path: semantic.path,
    scope: semantic.scope,
    side: semantic.scope === "range" ? semantic.side : preferredSide,
    startLine:
      semantic.scope === "range"
        ? semantic.startLine
        : ownedLines.length > 0
          ? Math.min(...ownedLines)
          : 1,
    endLine:
      semantic.scope === "range"
        ? semantic.endLine
        : ownedLines.length > 0
          ? Math.max(...ownedLines)
          : 1,
    changedLoc: compiled.changedLoc ?? 0,
    changedLineNumbers: ownedLines,
    contextHash: compiled.contextHash ?? "source-unavailable",
    nature: semantic.nature,
    concerns: semantic.concerns,
    scrutiny: semantic.scrutiny,
  };
}

/**
 * Split one semantic region into deterministic, review-sized focal stops. The
 * full file remains mounted and freely scrollable; a stop only controls the
 * current highlight and keyboard progression.
 */
export function buildTourStops(
  item: ResolvedGuideItem,
  options: { contextLines?: number; maxSpan?: number; maxChangedLines?: number; hunkGap?: number } = {},
): TourStop[] {
  // Keep a focus comfortably inside the code viewport. A larger focus can
  // contain changed lines the reviewer never sees before the next keypress.
  const maxSpan = Math.max(1, options.maxSpan ?? 28);
  const overlapLines = Math.min(maxSpan - 1, Math.max(0, options.contextLines ?? 6));
  const firstLine = Math.min(item.startLine, item.endLine);
  const lastLine = Math.max(item.startLine, item.endLine);
  const ranges: Array<{ startLine: number; endLine: number }> = [];
  const changedLines = [...new Set(item.changedLineNumbers)]
    .filter((line) => line >= firstLine && line <= lastLine)
    .sort((left, right) => left - right);

  if (changedLines.length === 0) {
    let cursor = firstLine;
    while (cursor <= lastLine) {
      const endLine = Math.min(lastLine, cursor + maxSpan - 1);
      ranges.push({ startLine: cursor, endLine });
      if (endLine === lastLine) break;
      cursor = endLine - overlapLines + 1;
    }
  } else {
    let changedIndex = 0;
    while (changedIndex < changedLines.length) {
      const anchor = changedLines[changedIndex]!;
      const previous = ranges.at(-1);
      const naturalStart = Math.max(firstLine, anchor - overlapLines);
      const startLine = previous
        ? Math.max(previous.endLine - overlapLines + 1, naturalStart)
        : naturalStart;
      const endLine = Math.min(lastLine, startLine + maxSpan - 1);
      ranges.push({ startLine, endLine });

      const nextIndex = changedLines.findIndex((line, index) => index > changedIndex && line > endLine);
      if (nextIndex < 0) break;
      changedIndex = nextIndex;
    }
  }

  return ranges.map((range, stopIndex) => ({
    ...item,
    ...range,
    stopId: `${item.contextHash}:${item.side}:${range.startLine}-${range.endLine}`,
    stopIndex,
    stopCount: ranges.length,
  }));
}
